<script setup lang="ts">
import { computed, useTemplateRef } from 'vue'
import { useResourceLabel } from '../../../common/composables/resource-names'
import { Area } from '../../../areas/models/area'

const {
    area,
} = defineProps<{
    area: Area
}>()

const popover = useTemplateRef('popover')
const togglePopover = (event: Event) => {
    if (!popover.value) {
        return
    }

    popover.value.toggle(event)
}

const address = computed(() => {
    return area.address ?? area.building?.address
})

const { singular: areaLabel } = useResourceLabel('areas')
const { singular: buildingLabel } = useResourceLabel('buildings')

const content = computed(() => {
    return {
        address: {
            tooltip: address.value ?? '',

            addressLine1: {
                label: 'Address Line 1',
                text: address.value?.addressLine1 ?? '',
            },

            addressLine2: {
                label: 'Address Line 2',
                text: address.value?.addressLine2 ?? '',
            },

            city: {
                label: 'City',
                text: address.value?.city ?? '',
            },

            postCode: {
                label: 'Post Code',
                text: address.value?.postCode ?? '',
            },

            state: {
                label: 'State',
                text: address.value?.state ?? '',
            },

            country: {
                label: 'Country',
                text: address.value?.country ?? '',
            },
        },
    }
})
</script>

<template>
    <div v-bind="$attrs" @mouseenter="togglePopover" @mouseleave="togglePopover">
        <slot>
            <div class="flex flex-col gap-y-1">
                <div class="text-sm text-muted-color font-semibold text-ellipsis whitespace-nowrap overflow-hidden">
                    {{ area.building?.name }}
                </div>
                <div class="text-ellipsis whitespace-nowrap overflow-hidden">
                    {{ area.name }}
                </div>
            </div>
        </slot>
    </div>

    <Popover ref="popover">
        <div class="flex flex-col gap-2">
            <div class="flex justify-between gap-x-2">
                <div class="text-muted-color">
                    {{ buildingLabel }}
                </div>

                <div class="font-semibold">
                    {{ area.building?.name }}
                </div>
            </div>

            <div class="flex justify-between gap-x-2">
                <div class="text-muted-color">
                    {{ areaLabel }}
                </div>

                <div class="font-semibold">
                    {{ area.name }}
                </div>
            </div>

            <div v-if="address">
                <Divider>
                    <span class="text-muted-color">Address</span>
                </Divider>

                <ul class="space-y-1 font-semibold">
                    <li v-if="address?.addressLine1">
                        {{ content.address.addressLine1.text }}
                    </li>
                    <li v-if="address?.addressLine2">
                        {{ content.address?.addressLine2.text }}
                    </li>
                    <li v-if="address?.city">
                        {{ content.address?.city.text }}
                    </li>
                    <li v-if="address?.postCode">
                        {{ content.address?.postCode.text }}
                    </li>
                    <li v-if="address?.state">
                        {{ content.address?.state.text }}
                    </li>
                    <li v-if="address?.country">
                        {{ content.address?.country.text }}
                    </li>
                </ul>
            </div>
        </div>
    </Popover>
</template>